import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, isSuperAdmin } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import CurrencySwitcher from '@/components/feature/CurrencySwitcher';
import DomainAlertBanner from '@/components/feature/DomainAlertBanner';
import useDomainMonitor from '@/hooks/useDomainMonitor';
import PlatformVersionBadge from '@/components/feature/PlatformVersionBadge';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import PlatformPermissionGuard from '@/components/feature/PlatformPermissionGuard';
import { usePlatformPermissions } from '@/hooks/usePlatformPermissions';
import usePlatformErrorAlerts from '@/hooks/usePlatformErrorAlerts';
import useFailedPaymentAlerts from '@/hooks/useFailedPaymentAlerts';
import { logPlatformActivity } from '@/lib/platformActivity';
import { avatarOr } from '@/lib/defaultImages';

interface NavItem {
  icon: string;
  label: string;
  tkey: string;
  path: string;
  permission: string | null;
}

const NAV_ITEMS: NavItem[] = [
  { icon: 'ri-dashboard-line', label: 'Tableau de bord', tkey: 'sa.dashboard', path: '/superadmin', permission: null },
  { icon: 'ri-bar-chart-box-line', label: 'Statistiques & vue globale', tkey: 'sa.stats', path: '/superadmin/stats', permission: null },
  { icon: 'ri-user-line', label: 'Utilisateurs', tkey: 'sa.users', path: '/superadmin/users', permission: 'users.read' },
  { icon: 'ri-admin-line', label: 'Équipe plateforme', tkey: 'sa.team', path: '/superadmin/team', permission: 'team.read' },
  { icon: 'ri-history-line', label: "Journal d'activité", tkey: 'sa.activity', path: '/superadmin/activity', permission: 'activity.read' },
  { icon: 'ri-organization-chart', label: 'Zifek Core', tkey: 'sa.core', path: '/superadmin/core', permission: 'core.read' },
  { icon: 'ri-palette-line', label: 'Theme Builder', tkey: 'sa.themes', path: '/superadmin/themes', permission: 'themes.read' },
  { icon: 'ri-book-open-line', label: 'Catégories de thèmes', tkey: 'sa.themeCategories', path: '/superadmin/themes/categories', permission: 'themes.read' },
  { icon: 'ri-apps-2-line', label: 'App Builder', tkey: 'sa.apps', path: '/superadmin/apps', permission: 'apps.read' },
  { icon: 'ri-graduation-cap-line', label: 'Formations', tkey: 'sa.formations', path: '/superadmin/formations', permission: 'formations.read' },
  { icon: 'ri-coin-line', label: 'Usage IA & Coûts', tkey: 'sa.aiUsage', path: '/superadmin/ai-usage', permission: 'ai.read' },
  { icon: 'ri-home-8-line', label: "Page d'accueil", tkey: 'sa.homepage', path: '/superadmin/homepage', permission: 'content.read' },
  { icon: 'ri-article-line', label: 'Blog', tkey: 'sa.blog', path: '/superadmin/blog', permission: null },
  { icon: 'ri-chat-quote-line', label: 'Témoignages', tkey: 'sa.testimonials', path: '/superadmin/temoignages', permission: null },
  { icon: 'ri-translate', label: 'Langues & Traductions', tkey: 'sa.translations', path: '/superadmin/translations', permission: 'translations.manage' },
  { icon: 'ri-customer-service-2-line', label: 'Support client', tkey: 'sa.support', path: '/superadmin/support', permission: 'support.read' },
  { icon: 'ri-global-line', label: 'Domaines personnalisés', tkey: 'sa.domains', path: '/superadmin/domains', permission: 'domains.read' },
  { icon: 'ri-price-tag-3-line', label: 'Types de compte', tkey: 'sa.accountTypes', path: '/superadmin/account-types', permission: 'accountTypes.read' },
  { icon: 'ri-share-forward-line', label: 'Réseaux', tkey: 'sa.reseaux', path: '/superadmin/reseaux', permission: null },
  { icon: 'ri-share-circle-line', label: 'Codes de parrainage', tkey: 'sa.parrainage', path: '/superadmin/parrainage', permission: null },
  { icon: 'ri-price-tag-2-line', label: "Formules d'abonnement", tkey: 'sa.plans', path: '/superadmin/plans', permission: null },
  { icon: 'ri-bill-line', label: 'Mes abonnements', tkey: 'sa.subscriptions', path: '/superadmin/subscriptions', permission: null },
  { icon: 'ri-secure-payment-line', label: 'Paiements plateforme', tkey: 'sa.payments', path: '/superadmin/payments', permission: null },
  { icon: 'ri-store-3-line', label: 'Paiement des commerces', tkey: 'sa.commercePayments', path: '/superadmin/commerce-payments', permission: null },
  { icon: 'ri-bank-card-2-line', label: 'Retraits commerces', tkey: 'sa.withdrawals', path: '/superadmin/withdrawals', permission: null },
  { icon: 'ri-webhook-line', label: 'Webhooks FeexPay', tkey: 'sa.webhooks', path: '/superadmin/webhooks/feexpay', permission: null },
  { icon: 'ri-database-2-line', label: 'Base de données & API', tkey: 'sa.database', path: '/superadmin/database', permission: 'database.read' },
  { icon: 'ri-terminal-line', label: 'Logs Système', tkey: 'sa.logs', path: '/superadmin/logs', permission: 'logs.read' },
  { icon: 'ri-hard-drive-3-line', label: 'Stockage', tkey: 'sa.storage', path: '/superadmin/storage', permission: 'storage.read' },
  { icon: 'ri-exchange-dollar-line', label: 'Taux de change', tkey: 'sa.rates', path: '/superadmin/rates', permission: 'rates.manage' },
  { icon: 'ri-smartphone-line', label: 'Application PWA', tkey: 'sa.pwa', path: '/superadmin/pwa', permission: 'pwa.read' },
  { icon: 'ri-shield-keyhole-line', label: 'Paramètres IA', tkey: 'sa.settings', path: '/superadmin/settings', permission: 'settings.read' },
];

export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const platform = usePlatformPermissions();
  // 🚨 Alertes de pic d'erreurs : compteur live visible partout dans le back-office.
  // On n'interroge le journal qu'une fois la session SuperAdmin établie.
  const alertsEnabled = !!user && isSuperAdmin(user.typecompte);
  const { count: errorAlertCount } = usePlatformErrorAlerts(60000, alertsEnabled);
  // 💳 Pastille « paiement échoué » : lien direct vers la ligne du journal.
  const { count: failedPaymentCount, lastId: lastFailedPaymentId, last: lastFailedPayment } = useFailedPaymentAlerts(60000, alertsEnabled);
  const failedPaymentLink = `/superadmin/logs?tab=payments${lastFailedPaymentId ? `&attempt=${lastFailedPaymentId}` : ''}`;

  const visibleNav = NAV_ITEMS.filter((item) => platform.has(item.permission));

  // Vérification périodique automatique de TOUS les domaines personnalisés.
  const domainMonitor = useDomainMonitor({ userId: user?.id, userEmail: user?.email, mode: 'admin' });

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSuperAdmin(user.typecompte)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Journal d'activité : on trace la connexion des travailleurs plateforme
  // (les superadmins principaux ne sont pas concernés).
  useEffect(() => {
    if (!user || !platform.isStaff) return;
    const key = `zifek-platform-login-${user.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* stockage indisponible */
    }
    void logPlatformActivity({
      actorId: user.id,
      actorName: user.name,
      actorRole: platform.roleLabel,
      action: 'login',
      description: `Connexion au back-office (${platform.roleLabel || 'travailleur plateforme'})`,
    });
  }, [user, platform.isStaff, platform.roleLabel]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return null;
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background-100 flex">
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-[100dvh] w-72 max-w-[85vw] bg-background-50 border-r border-background-200/70 transition-transform duration-200 lg:w-64 lg:max-w-none lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo - fixed at top */}
          <div className="flex-shrink-0 p-5 pb-3">
            <div className="flex items-center justify-between gap-2 mb-6">
              <Link to="/superadmin" className="flex items-center gap-2 min-w-0">
                {brand.logo ? (
                  <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-50 border border-background-200/50 flex items-center justify-center flex-shrink-0">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="w-full h-full object-contain p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center flex-shrink-0">
                    <span className="text-background-50 font-bold text-sm font-heading">
                      {brand.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-lg font-bold font-heading text-foreground-950">
                    {brand.name}
                  </span>
                  <span className="block text-[10px] text-foreground-400 -mt-0.5">SuperAdmin</span>
                </div>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer shrink-0"
                title="Fermer le menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Navigation - scrollable */}
          <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 pb-4 overscroll-contain" style={{ scrollbarWidth: 'thin', scrollbarColor: 'oklch(var(--foreground-200) / 0.5) transparent' }}>
            <div className="space-y-0.5">
              {visibleNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    isActive(item.path)
                      ? 'bg-foreground-100 text-foreground-900 font-semibold'
                      : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                  }`}
                >
                  <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
                  <span className="leading-snug flex-1">{t(item.tkey, { defaultValue: item.label })}</span>
                  {item.path === '/superadmin/logs' && errorAlertCount > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-background-50 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {errorAlertCount > 99 ? '99+' : errorAlertCount}
                    </span>
                  )}
                  {item.path === '/superadmin/logs' && failedPaymentCount > 0 && (
                    <span
                      className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-background-50 text-[10px] font-bold flex items-center justify-center flex-shrink-0"
                      title={`${failedPaymentCount} paiement(s) échoué(s) sur 24 h`}
                    >
                      <i className="ri-secure-payment-line text-[10px]"></i>
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </nav>

          {/* User section - fixed at bottom */}
          <div className="flex-shrink-0 p-5 pt-3 border-t border-background-200/70">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-foreground-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src={avatarOr(user.image)} alt={user.name} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground-950 truncate">{user.name}</div>
                <div className="text-xs text-foreground-500 truncate">{platform.roleLabel || 'Super Admin'}</div>
              </div>
            </div>
            <Link
              to="/dashboard"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer mb-2"
            >
              <i className="ri-store-2-line w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
              Mon dashboard
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
              Déconnexion
            </button>
            <div className="pt-3 mt-1 border-t border-background-200/60">
              <PlatformVersionBadge />
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 pb-16 lg:pb-0">
        <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="flex items-center justify-between h-14 md:h-16 px-4 md:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-200/70 cursor-pointer"
              >
                <i className="ri-menu-line text-lg text-foreground-700"></i>
              </button>
              <h1 className="text-lg font-bold font-heading text-foreground-950">
                SuperAdmin
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {failedPaymentCount > 0 && (
                <Link
                  to={failedPaymentLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors whitespace-nowrap"
                  title={`${failedPaymentCount} paiement(s) échoué(s) sur 24 h${lastFailedPayment?.provider ? ` — dernier : ${lastFailedPayment.provider}` : ''}. Ouvrir la ligne du journal.`}
                >
                  <i className="ri-secure-payment-line"></i>
                  {failedPaymentCount} paiement{failedPaymentCount > 1 ? 's' : ''} échoué{failedPaymentCount > 1 ? 's' : ''}
                </Link>
              )}
              {errorAlertCount > 0 && (
                <Link
                  to="/superadmin/logs"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors whitespace-nowrap"
                  title="Pics d'erreurs détectés"
                >
                  <i className="ri-alarm-warning-line"></i>
                  {errorAlertCount} alerte{errorAlertCount > 1 ? 's' : ''}
                </Link>
              )}
              <LanguageSwitcher compact />
              <CurrencySwitcher compact nativeCurrency={user?.monaie || 'MAD'} />
              <Link
                to="/dashboard"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-store-2-line"></i>
                <span>Dashboard</span>
              </Link>
            </div>
          </div>
        </header>

        <main>
          {domainMonitor.alert && (
            <div className="px-4 md:px-6 pt-4">
              <DomainAlertBanner
                alert={domainMonitor.alert}
                to="/superadmin/domains"
                label="Voir les domaines"
              />
            </div>
          )}
          <PlatformPermissionGuard>
            <Outlet />
          </PlatformPermissionGuard>
        </main>
      </div>

      {/* Navigation mobile (barre inférieure) */}
      <MobileBottomNav
        items={[
          { icon: 'ri-dashboard-line', label: 'Tableau de bord', tkey: 'sa.dashboard', to: '/superadmin', active: isActive('/superadmin') },
          ...(platform.has('users.read') ? [{ icon: 'ri-user-line', label: 'Utilisateurs', tkey: 'sa.users', to: '/superadmin/users', active: isActive('/superadmin/users') }] : []),
          ...(platform.has('core.read') ? [{ icon: 'ri-organization-chart', label: 'Zifek Core', tkey: 'sa.core', to: '/superadmin/core', active: isActive('/superadmin/core') }] : []),
          ...(platform.has('themes.read') ? [{ icon: 'ri-palette-line', label: 'Thèmes', tkey: 'sa.themes', to: '/superadmin/themes', active: isActive('/superadmin/themes') }] : []),
        ]}
        onMore={() => setMobileMenuOpen(true)}
        moreLabel="Plus"
      />
    </div>
  );
}